import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Save, Share2, Plus, Trash2, MessageCircle, Printer, Package, Upload, CheckCircle, XCircle, Wrench, Search, Check, ChevronsUpDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useState, useMemo, useEffect } from "react";
import { createQuote, generateQuoteCode } from "@/integrations/supabase/quotes";
import { getProducts } from "@/integrations/supabase/products";
import { listServicos } from "@/integrations/supabase/servicos";
import { useAuth } from "@/components/AuthProvider";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useSync } from "@/contexts/SyncContext";
import { useSyncOperations } from "@/hooks/useSyncOperations";
import { validateName, validateMoney, validateDescription, validateForm } from "@/utils/validators";
import { errorHandler } from "@/utils/errorHandler";
import { logger } from "@/utils/logger";
import { performanceMonitor } from "@/utils/performanceMonitor";
import { ClientSearch } from "@/components/ClientSearch";
import { PersonalizationListEditor, PersonalizationEntry } from "@/components/PersonalizationListEditor";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";
import ArquivosSectionLocal from "@/components/ArquivosSectionLocal";
import { addArquivoToRecord, ArquivoData } from "@/integrations/supabase/arquivos";
import { supabase } from "@/integrations/supabase/client";
import { uploadQuoteFile } from "@/integrations/supabase/storage";
import { CLOTHING_SIZES } from "@/constants/sizes";
import { cn } from "@/lib/utils";
import { isoInputToBR } from "@/utils/dateOnly";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { getOrderTypes, createOrderType } from "@/integrations/supabase/taxonomies";
import { AddOrderTypeDialog } from "@/components/AddOrderTypeDialog";
import { SpecificationFields, SpecificationDisplay } from "@/components/SpecificationFields";
import { getProductCategories } from "@/integrations/supabase/taxonomies";
import { getOnboardingProgress, completeOnboardingStep } from "@/integrations/supabase/onboarding";
import { ONBOARDING_STEPS } from "@/constants/onboarding";
import { NewProductModal } from "@/components/NewProductModal";
import { useClothingSizes } from "@/hooks/useClothingSizes";
import { AddCustomSizeDialog } from "@/components/AddCustomSizeDialog";

export default function NovoOrcamento() {
  const navigate = useNavigate();
  const { empresa, user } = useAuth();
  const queryClient = useQueryClient();
  const { invalidateRelated } = useSync();
  const { syncAfterCreate } = useSyncOperations();
  const { sizes: clothingSizes, addSize: addCustomSize, isLoading: isSizesLoading } = useClothingSizes(empresa?.id);

  // Gerar código uma única vez quando o componente monta
  const code = useMemo(() => generateQuoteCode(), []);

  const [items, setItems] = useState([{ description: "", quantity: 1, value: 0, category: "", especificacoes: {} }]);
  const [selectedProduct, setSelectedProduct] = useState<string>("");
  const [selectedServico, setSelectedServico] = useState<string>("");
  const [productSearchTerm, setProductSearchTerm] = useState<string>("");
  const [productPopoverOpen, setProductPopoverOpen] = useState(false);
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<string>("A combinar");
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [newClientName, setNewClientName] = useState("");
  const [newClientPhone, setNewClientPhone] = useState("");
  const [newClientEmail, setNewClientEmail] = useState("");
  const [newClientCpfCnpj, setNewClientCpfCnpj] = useState("");
  const [newClientAddress, setNewClientAddress] = useState("");
  const [newClientEnderecoLogradouro, setNewClientEnderecoLogradouro] = useState("");
  const [newClientEnderecoNumero, setNewClientEnderecoNumero] = useState("");
  const [newClientEnderecoComplemento, setNewClientEnderecoComplemento] = useState("");
  const [newClientEnderecoBairro, setNewClientEnderecoBairro] = useState("");
  const [newClientEnderecoCidade, setNewClientEnderecoCidade] = useState("");
  const [newClientEnderecoUf, setNewClientEnderecoUf] = useState("");
  const [newClientEnderecoCep, setNewClientEnderecoCep] = useState("");
  const [creatingClient, setCreatingClient] = useState(false);
  const [quantityModalOpen, setQuantityModalOpen] = useState(false);
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);
  const [customMessage, setCustomMessage] = useState("");
  const [personalizations, setPersonalizations] = useState<PersonalizationEntry[]>([]);

  // Novos estados para campos do pedido
  const [type, setType] = useState<string>("");
  const [selectedMedida, setSelectedMedida] = useState<string>("");
  const [medidas, setMedidas] = useState<any[]>([]);
  const [buscandoMedidas, setBuscandoMedidas] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [lastSavedCode, setLastSavedCode] = useState<string | null>(null);
  const [arquivos, setArquivos] = useState<ArquivoData[]>([]);
  const [isLoadingArquivos, setIsLoadingArquivos] = useState(false);
  const [isKitMode, setIsKitMode] = useState<boolean>(false);
  const [kitItems, setKitItems] = useState<Array<{size: string, quantity: number}>>([
    { size: "P", quantity: 0 },
    { size: "M", quantity: 0 },
    { size: "G", quantity: 0 },
    { size: "GG", quantity: 0 }
  ]);
  const [size, setSize] = useState<string>("");
  const [color, setColor] = useState<string>("");
  const [quantity, setQuantity] = useState<number>(1);
  const [discount, setDiscount] = useState<number>(0);
  const [discountType, setDiscountType] = useState<'fixed' | 'percentage'>('fixed');
  const [currentItemSpecificacoes, setCurrentItemSpecificacoes] = useState<Record<string, any>>({});
  const [currentItemCategory, setCurrentItemCategory] = useState<string>("");

  // Novo estado para condições de pagamento (separado)
  const [paymentConditions, setPaymentConditions] = useState<string>("");

  // Query para buscar produtos do catálogo
  const { data: products = [] } = useQuery({
    queryKey: ["products"],
    queryFn: getProducts,
  });

  // Query para buscar categorias
  const { data: categories = [] } = useQuery({
    queryKey: ["product-categories", empresa?.id],
    queryFn: async () => {
      if (!empresa?.id) return [];
      return await getProductCategories(empresa.id);
    },
    enabled: !!empresa?.id,
  });

  // Query para verificar progresso do onboarding
  const { data: onboardingProgress } = useQuery({
    queryKey: ['onboarding-progress', empresa?.id],
    queryFn: () => getOnboardingProgress(empresa?.id!),
    enabled: !!empresa?.id,
  });

  // Query para buscar tipos de pedido dinâmicos
  const { data: orderTypesData = [], refetch: refetchOrderTypes } = useQuery({
    queryKey: ["orderTypes", empresa?.id],
    queryFn: async () => {
      if (!empresa?.id) return [];
      try {
        const types = await getOrderTypes(empresa.id);
        logger.debug(`📋 Tipos de pedido carregados: ${types.map(t => t.name).join(", ")}`);
        return types;
      } catch (error) {
        logger.error("❌ Erro ao carregar tipos de pedido:", error);
        return [];
      }
    },
    enabled: !!empresa?.id,
  });

  // Array de tipos padrão + tipos customizados
  const allOrderTypes = [
    "bordado",
    "camiseta",
    "uniforme",
    "personalizado",
    "catalogo",
    "conserto",
    "ajuste",
    "outro",
    ...orderTypesData.map(t => t.name)
  ];

  // Função para adicionar novo tipo de pedido
  const handleAddOrderType = async (typeName: string) => {
    if (!empresa?.id || !user?.id) {
      toast.error("Erro ao identificar empresa");
      return;
    }

    try {
      await createOrderType(empresa.id, typeName, user.id);
      await refetchOrderTypes();
      toast.success("Tipo de pedido adicionado com sucesso!");
    } catch (error) {
      logger.error("❌ Erro ao adicionar tipo de pedido:", error);
      toast.error("Erro ao adicionar tipo de pedido");
    }
  };

  // Normalizar texto para busca (remove acentos, espaços extras, caixa)
  const normalizeSearch = (value: string) =>
    value
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .replace(/\s+/g, " ")
      .trim();

  // Filtrar produtos baseado no termo de busca
  const filteredProducts = useMemo(() => {
    if (!productSearchTerm.trim()) {
      return products;
    }

    const search = normalizeSearch(productSearchTerm);
    if (!search) return products;

    const searchWords = search.split(" ");

    return products.filter((product) => {
      const base = [
        product.name,
        product.type || "",
        ...(Array.isArray(product.materials) ? product.materials : []),
      ]
        .join(" ")
        .toString();

      const normalizedProduct = normalizeSearch(base);

      // Cada palavra digitada deve existir em alguma parte do texto do produto
      return searchWords.every((word) => normalizedProduct.includes(word));
    });
  }, [products, productSearchTerm]);

  // Carregar orçamento duplicado do localStorage se existir
  useEffect(() => {
    const duplicateQuoteData = localStorage.getItem('duplicateQuote');
    if (duplicateQuoteData) {
      try {
        const quoteData = JSON.parse(duplicateQuoteData);
        setClientName(quoteData.client || "");
        setClientPhone(quoteData.phone || "");

        // Carregar items se disponível, senão usar observations
        if (quoteData.items && Array.isArray(quoteData.items) && quoteData.items.length > 0) {
          setItems(quoteData.items);
        } else if (quoteData.observations) {
          setItems([{ description: quoteData.observations, quantity: 1, value: quoteData.value || 0, category: "", especificacoes: {} }]);
        }

        // Carregar desconto se disponível
        if (quoteData.discount) {
          setDiscount(quoteData.discount);
        }
        if (quoteData.discount_type) {
          setDiscountType(quoteData.discount_type);
        }

        // Limpar localStorage para não duplicar novamente
        localStorage.removeItem('duplicateQuote');
        toast.success("Dados do orçamento duplicado carregados!");
      } catch (error) {
        console.error("Erro ao carregar dados duplicados:", error);
        localStorage.removeItem('duplicateQuote');
      }
    }
  }, []);

  // Buscar serviços
  const { data: servicos = [] } = useQuery({
    queryKey: ["servicos"],
    queryFn: () => listServicos({ ativo: true }),
  });

  // Função para buscar medidas do cliente
  const buscarMedidasCliente = async (nomeCliente: string) => {
    if (!nomeCliente || nomeCliente.length < 3) {
      setMedidas([]);
      setBuscandoMedidas(false);
      return;
    }

    setBuscandoMedidas(true);

    try {
      const { data: medidasEncontradas, error } = await supabase
        .from('atelie_medidas')
        .select('*')
        .ilike('cliente_nome', `%${nomeCliente}%`)
        .order('created_at', { ascending: false });

      if (error) {
        console.error("Erro ao buscar medidas:", error);
        setMedidas([]);
        return;
      }

      setMedidas(medidasEncontradas || []);
    } catch (error) {
      console.error("Erro ao buscar medidas:", error);
      setMedidas([]);
    } finally {
      setBuscandoMedidas(false);
    }
  };

  // Funções do Modo Kit
  const addKitSize = () => {
    setKitItems([...kitItems, { size: "", quantity: 0 }]);
  };

  const removeKitSize = (index: number) => {
    setKitItems(kitItems.filter((_, i) => i !== index));
  };

  const updateKitSize = (index: number, size: string) => {
    const updated = [...kitItems];
    updated[index].size = size;
    setKitItems(updated);
  };

  const updateKitQuantity = (index: number, quantity: number) => {
    const updated = [...kitItems];
    updated[index].quantity = quantity;
    setKitItems(updated);
  };

  const getTotalKitQuantity = () => {
    return kitItems.reduce((total, item) => total + item.quantity, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("=== NOVO ORÇAMENTO SENDO SALVO ===");
    console.log("Arquivos no state:", arquivos.length);
    if (arquivos.length > 0) {
      console.log("Arquivos:", arquivos.map(a => a.nome).join(", "));
    }

    const dateInput = (document.getElementById("date") as HTMLInputElement)?.value || new Date().toISOString().split('T')[0];
    const deliveryDateInput = (document.getElementById("deliveryDate") as HTMLInputElement)?.value;
    const observations = (document.getElementById("observations") as HTMLTextAreaElement)?.value || undefined;
    const code = generateQuoteCode();
    
    // Montar observações com informações adicionais
    let observationsText = observations || '';
    if (type) {
      observationsText += (observationsText ? '\n' : '') + `Tipo: ${type}`;
    }
    if (size) {
      observationsText += (observationsText ? '\n' : '') + `Tamanho: ${size}`;
    }
    if (color) {
      observationsText += (observationsText ? '\n' : '') + `Cor: ${color}`;
    }
    if (quantity > 1) {
      observationsText += (observationsText ? '\n' : '') + `Quantidade: ${quantity}`;
    }
    if (isKitMode && getTotalKitQuantity() > 0) {
      const kitInfo = kitItems
        .filter(item => item.quantity > 0)
        .map(item => `${item.quantity} ${item.size}`)
        .join(', ');
      if (kitInfo) {
        observationsText += (observationsText ? '\n' : '') + `Kit: ${kitInfo}`;
      }
    }
    if (deliveryDateInput) {
      observationsText += (observationsText ? '\n' : '') + `Data de entrega estimada: ${isoInputToBR(deliveryDateInput)}`;
    }

    // Adicionar condições de pagamento se preenchidas
    let finalObservationsText = observationsText;
    if (paymentConditions.trim()) {
      finalObservationsText += (finalObservationsText ? '\n\n' : '') + `📋 CONDIÇÕES DE PAGAMENTO:\n${paymentConditions}`;
    }

    // Validação de campos obrigatórios
    // Validação robusta
    const validation = validateForm(
      { client: clientName, items },
      {
        client: validateName,
        items: (items) => {
          if (!Array.isArray(items) || items.length === 0) {
            return { isValid: false, errors: ['Adicione pelo menos um item ao orçamento'] };
          }
          
          const errors: string[] = [];
          items.forEach((item, index) => {
            if (!item.quantity || item.quantity <= 0) {
              errors.push(`Item ${index + 1}: Quantidade deve ser maior que zero`);
            }
            if (!item.value || item.value <= 0) {
              errors.push(`Item ${index + 1}: Valor deve ser maior que zero`);
            }
          });
          
          return { isValid: errors.length === 0, errors };
        }
      }
    );
    
    if (!validation.isValid) {
      validation.errors.forEach(error => toast.error(error));
      return;
    }

    // DEBUG: Log dos items antes de salvar
    console.log("🔍 DEBUG - Items antes de salvar:", JSON.stringify(items, null, 2));
    items.forEach((item, idx) => {
      console.log(`Item ${idx}:`, {
        description: item.description,
        quantity: item.quantity,
        value: item.value,
        category: item.category,
        especificacoes: item.especificacoes
      });
    });

    // Medir performance e criar orçamento
    const result = await performanceMonitor.measure(
      'createQuote',
      async () => {
        console.log("📤 Enviando para createQuote com items:", items);
        return await createQuote({
          code,
          customer_name: clientName,
          customer_phone: clientPhone,
          date: dateInput,
          observations: finalObservationsText || undefined,
          discount: discount,
          discount_type: discountType,
          items,
          personalizations: personalizations
            .filter((p) => p.personName.trim())
            .map((p) => ({
              person_name: p.personName.trim(),
              size: p.size?.trim() || undefined,
              quantity: p.quantity ?? 1,
              notes: p.notes?.trim() || undefined,
            })),
        });
      },
      'NovoOrcamento'
    );

    if (!result.ok) {
      const appError = errorHandler.handleSupabaseError(
        { message: result.error, code: 'CREATE_QUOTE_ERROR' },
        'createQuote'
      );
      logger.error('Falha ao criar orçamento', 'NOVO_ORCAMENTO', { client: clientName, itemsCount: items.length, error: result.error });
      toast.error(appError.message);
      return;
    }

    console.log("✅ [NovoOrcamento] Orçamento criado com sucesso. Arquivos pendentes:", {
      arquivosCount: arquivos.length,
      resultId: result.id,
      arquivos: arquivos.map(a => ({ nome: a.nome, posicao: a.posicao }))
    });

    // Log de sucesso
    logger.userAction('quote_created', 'NOVO_ORCAMENTO', {
      quoteCode: code,
      client: clientName,
      itemsCount: items.length,
      totalValue: items.reduce((sum, item) => sum + (item.quantity * item.value), 0),
      personalizationCount: personalizations.length,
    });

    setLastSavedCode(code);

    // Sincronizar arquivos para o banco de dados
    console.log("━".repeat(60));
    console.log("🔍 [NovoOrcamento] Verificando sincronização de arquivos:", {
      arquivosCount: arquivos.length,
      resultId: result.id,
      willSync: arquivos.length > 0 && result.id,
    });

    if (arquivos.length > 0 && result.id) {
      try {
        console.log("📤 [NovoOrcamento] Iniciando sincronização de", arquivos.length, "arquivo(s) para ID:", result.id);
        for (const arquivo of arquivos) {
          console.log("💾 [NovoOrcamento] Salvando arquivo:", arquivo.nome, {
            id: arquivo.id,
            posicao: arquivo.posicao,
            tamanho_kb: arquivo.tamanho_kb,
            url: arquivo.url?.substring(0, 50) + "..."
          });
          const syncResult = await addArquivoToRecord('quotes', result.id, arquivo);
          console.log(`✅ Arquivo sincronizado:`, {
            nome: arquivo.nome,
            resultOk: syncResult.ok,
            resultError: syncResult.error
          });
          if (!syncResult.ok) {
            console.error(`❌ ERRO ao sincronizar arquivo ${arquivo.nome}:`, syncResult.error);
            throw new Error(`Falha ao sincronizar ${arquivo.nome}: ${syncResult.error}`);
          }
        }
        console.log(`✅ SUCESSO! ${arquivos.length} arquivo(s) sincronizado(s) com sucesso`);
      } catch (error: any) {
        console.error("⚠️ ERRO CRÍTICO ao sincronizar arquivos:", error);
        toast.error(`Erro ao salvar os arquivos: ${error.message}`);
      }
    } else {
      console.log("⚠️ [NovoOrcamento] Arquivo(s) não sincronizado(s).", {
        arquivosCount: arquivos.length,
        resultId: result.id,
        reason: arquivos.length === 0 ? "Nenhum arquivo no state" : "result.id não disponível"
      });
    }
    console.log("━".repeat(60));

    // Navegar imediatamente sem atualizações de estado para evitar erros de removeChild
    // As invalidações serão feitas na página de destino
    const targetUrl = `/orcamento/${code}`;

    // Auto-marcar etapa de primeiro orçamento no onboarding
    if (empresa?.id && onboardingProgress?.shouldShowChecklist) {
      completeOnboardingStep(empresa.id, ONBOARDING_STEPS.FIRST_QUOTE);
      queryClient.invalidateQueries({ queryKey: ['onboarding-progress', empresa.id] });
    }

    // Exibir toast e navegar imediatamente sem esperar atualizações de estado
    toast.success("✅ Orçamento criado com sucesso! Etapa 3 concluída!", {
      duration: 2000,
    });

    // Proteção contra erros de removeChild: usar window.location.replace
    // que não adiciona entrada no histórico e força limpeza completa
    // Além disso, envolver em try-catch silencioso para ignorar erros de DOM
    try {
      // Fechar modais de forma síncrona antes de navegar
      setProductPopoverOpen(false);
      setQuantityModalOpen(false);
      setWhatsappModalOpen(false);
    } catch (e) {
      // Ignorar erros ao fechar modais
    }

    // Usar window.location.replace em vez de href para evitar problemas com histórico
    // e garantir limpeza completa do estado React
    // IMPORTANTE: Navega imediatamente após sincronização de arquivos (sem setTimeout!)
    try {
      window.location.replace(targetUrl);
    } catch (error) {
      // Se ainda houver erro, tentar href como fallback
      console.warn('Erro ao usar replace, tentando href:', error);
      window.location.href = targetUrl;
    }
  };

  const handlePrint = () => {
    if (!lastSavedCode) {
      toast.info("Salve o orçamento antes de imprimir o PDF completo.");
      return;
    }

    const printUrl = `${window.location.origin}/orcamentos/${lastSavedCode}/impressao`;
    window.open(printUrl, "_blank");
  };

  const addItem = () => {
    setItems([...items, { description: "", quantity: 1, value: 0, category: currentItemCategory, especificacoes: currentItemSpecificacoes }]);
    setCurrentItemSpecificacoes({});
    setCurrentItemCategory("");
  };

  // Função para abrir modal de quantidade
  const openQuantityModal = () => {
    if (!selectedProduct) {
      toast.error("Selecione um produto do catálogo");
      return;
    }
    setQuantityModalOpen(true);
  };

  // Função para adicionar item do catálogo com quantidade
  const addCatalogItemWithQuantity = () => {
    const product = products.find(p => p.id === selectedProduct);
    if (!product) {
      toast.error("Produto não encontrado");
      return;
    }

    const newItem = {
      description: `${product.name} - ${product.type}`,
      quantity: selectedQuantity,
      value: product.unit_price,
      category: currentItemCategory || product.type || "",
      especificacoes: currentItemSpecificacoes || {}
    };

    setItems([...items, newItem]);
    setSelectedProduct(""); // Limpar seleção
    setQuantityModalOpen(false);
    setSelectedQuantity(1); // Reset quantidade
    setCurrentItemCategory(""); // Reset categoria
    setCurrentItemSpecificacoes({}); // Reset especificações
    toast.success("Produto adicionado ao orçamento!");
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const subtotal = items.reduce((sum, item) => sum + item.quantity * item.value, 0);
  const discountAmount = discountType === 'percentage'
    ? (subtotal * discount / 100)
    : discount;
  const total = Math.max(0, subtotal - discountAmount);

  const handleOpenPublic = () => {
    const code = generateQuoteCode();
    window.open(`${window.location.origin}/orcamento/${code}`, "_blank");
  };

  const generateDefaultMessage = () => {
    return `Olá ${clientName}! 

Segue o seu orçamento do ${empresa?.nome || 'Ateliê'}:

${items.map((item, index) => 
  `${index + 1}. ${item.description} - Qtd: ${item.quantity} - R$ ${item.value.toFixed(2)}`
).join('\n')}

*Total: R$ ${total.toFixed(2)}*

Aguardo seu retorno! 😊`;
  };

  const handleWhatsApp = () => {
    setCustomMessage(generateDefaultMessage());
    setWhatsappModalOpen(true);
  };

  const sendWhatsAppMessage = () => {
    const message = encodeURIComponent(customMessage);
    window.open(`https://wa.me/?text=${message}`, "_blank");
    setWhatsappModalOpen(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="flex items-center gap-4 p-4">
          <SidebarTrigger />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/orcamentos")}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Novo Orçamento</h1>
            <p className="text-sm text-muted-foreground">Crie um orçamento rápido</p>
          </div>
        </div>
      </header>

      <div className="p-6 max-w-4xl mx-auto space-y-6">
        <OnboardingChecklist />
        <form onSubmit={handleSubmit}>
          <Card className="border-border animate-fade-in">
            <CardHeader>
              <CardTitle className="text-foreground">Informações do Orçamento</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ClientSearch
                  value={clientName}
                  onChange={(value) => {
                    setClientName(value);
                    buscarMedidasCliente(value);
                  }}
                  onPhoneChange={setClientPhone}
                  onCreateNew={(name) => {
                    setNewClientName(name);
                    setShowNewClientModal(true);
                  }}
                  placeholder="Nome do cliente"
                  required
                />

                <div className="space-y-2">
                  <Label htmlFor="phone">Telefone</Label>
                  <Input
                    id="phone"
                    value={clientPhone}
                    onChange={(e) => setClientPhone(e.target.value)}
                    placeholder="(11) 99999-9999"
                    className="border-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date">Data do Orçamento</Label>
                  <Input
                    id="date"
                    type="date"
                    defaultValue={new Date().toISOString().split('T')[0]}
                    className="border-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="deliveryDate">Data de Entrega Estimada</Label>
                  <Input
                    id="deliveryDate"
                    type="date"
                    className="border-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="paymentMethod">Forma de Pagamento</Label>
                  <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                    <SelectTrigger className="border-input">
                      <SelectValue placeholder="Selecione (opcional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A combinar">A combinar</SelectItem>
                      <SelectItem value="Pix">Pix</SelectItem>
                      <SelectItem value="Dinheiro">Dinheiro</SelectItem>
                      <SelectItem value="Cartão de Débito">Cartão de Débito</SelectItem>
                      <SelectItem value="Cartão de Crédito">Cartão de Crédito</SelectItem>
                      <SelectItem value="Transferência">Transferência</SelectItem>
                      <SelectItem value="Boleto">Boleto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="paymentConditions">Condições de Pagamento</Label>
                  <Textarea
                    id="paymentConditions"
                    placeholder="Ex: 50% entrada, 50% na retirada; Parcelado em 3x; PRAZO ESTIMADO..."
                    value={paymentConditions}
                    onChange={(e) => setPaymentConditions(e.target.value)}
                    rows={2}
                    className="border-input resize-none"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="type">Tipo de Pedido</Label>
                  <AddOrderTypeDialog 
                    onAddType={handleAddOrderType}
                    existingTypes={allOrderTypes}
                    trigger={
                      <Button 
                        type="button"
                        variant="outline" 
                        size="sm" 
                        className="h-8 w-8 p-0"
                        title="Adicionar novo tipo de pedido"
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    }
                  />
                </div>
                <Select value={type} onValueChange={(value) => {
                  setType(value);
                  if (value !== "catalogo") {
                    setSelectedProduct("");
                  }
                }}>
                  <SelectTrigger className="border-input">
                    <SelectValue placeholder="Selecione o tipo (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bordado">Bordado Computadorizado</SelectItem>
                    <SelectItem value="camiseta">Camiseta Personalizada</SelectItem>
                    <SelectItem value="uniforme">Uniforme</SelectItem>
                    <SelectItem value="personalizado">Personalizado</SelectItem>
                    <SelectItem value="catalogo">Item do Catálogo</SelectItem>
                    <SelectItem value="outro">Outro</SelectItem>
                    {orderTypesData.map(type => (
                      <SelectItem key={type.id} value={type.name}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Seletor de Medidas */}
              <div className="space-y-2">
                <Label htmlFor="medidas">Medidas do Cliente</Label>
                {buscandoMedidas ? (
                  <div className="flex items-center gap-2 p-3 bg-blue-50 rounded border border-blue-200">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span className="text-sm text-blue-600">Buscando medidas...</span>
                  </div>
                ) : medidas.length > 0 ? (
                  <Select value={selectedMedida} onValueChange={setSelectedMedida}>
                    <SelectTrigger className="border-input">
                      <SelectValue placeholder="Selecione as medidas do cliente (opcional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {medidas.map((medida) => (
                        <SelectItem key={medida.id} value={medida.id}>
                          {medida.tipo_peca.toUpperCase()} - {medida.cliente_nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="text-sm text-gray-500 p-3 bg-gray-50 rounded border border-gray-200">
                    Nenhuma medida encontrada para este cliente. 
                    <br />
                    <span className="text-xs">Cadastre medidas na página "Medidas" primeiro.</span>
                  </div>
                )}
                {selectedMedida && medidas.length > 0 && (
                  <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded border border-blue-200">
                    {(() => {
                      const medida = medidas.find(m => m.id === selectedMedida);
                      return medida ? (
                        <div>
                          <p className="font-medium text-blue-800 mb-2">Medidas Selecionadas:</p>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            {medida.busto && <span><strong>Busto:</strong> {medida.busto}cm</span>}
                            {medida.cintura && <span><strong>Cintura:</strong> {medida.cintura}cm</span>}
                            {medida.quadril && <span><strong>Quadril:</strong> {medida.quadril}cm</span>}
                            {medida.ombro && <span><strong>Ombro:</strong> {medida.ombro}cm</span>}
                            {medida.coxa && <span><strong>Coxa:</strong> {medida.coxa}cm</span>}
                            {medida.comprimento && <span><strong>Comprimento:</strong> {medida.comprimento}cm</span>}
                          </div>
                          {medida.observacoes && (
                            <p className="mt-2 text-xs"><strong>Observações:</strong> {medida.observacoes}</p>
                          )}
                        </div>
                      ) : null;
                    })()}
                  </div>
                )}
              </div>

              {/* Arquivo / Arte - Seção melhorada com suporte a múltiplos uploads */}
              <ArquivosSectionLocal
                arquivos={arquivos}
                onArquivosUpdated={setArquivos}
                isExpanded={true}
              />

              {/* Campos de Tamanho, Cor e Quantidade */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="qty">Quantidade</Label>
                  <Input 
                    id="qty" 
                    type="number" 
                    min="1" 
                    value={quantity} 
                    onChange={(e) => setQuantity(Number(e.target.value))} 
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <Label htmlFor="size">Tamanho</Label>
                    <AddCustomSizeDialog
                      onAddSize={addCustomSize}
                      isLoading={isSizesLoading}
                      trigger={
                        <button
                          type="button"
                          className="text-xs px-2 py-1 border border-input rounded hover:bg-accent text-muted-foreground"
                          title="Adicionar novo tamanho"
                        >
                          + Novo
                        </button>
                      }
                    />
                  </div>
                  <Select value={size} onValueChange={setSize}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tamanho" />
                    </SelectTrigger>
                    <SelectContent>
                      {clothingSizes.map((sizeOption) => (
                        <SelectItem key={sizeOption.value} value={sizeOption.value}>
                          {sizeOption.label}{sizeOption.isCustom ? " (Custom)" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="color">Cor</Label>
                  <Input 
                    id="color" 
                    placeholder="Ex.: Preto, Azul..." 
                    value={color} 
                    onChange={(e) => setColor(e.target.value)} 
                  />
                </div>
              </div>

              {/* Controle de Kits e Tamanhos */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">Controle de Kits e Tamanhos</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setIsKitMode(!isKitMode)}
                    className="text-xs"
                  >
                    {isKitMode ? "Modo Simples" : "Modo Kit"}
                  </Button>
                </div>

                {isKitMode && (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Ideal para uniformes e lotes com diferentes tamanhos
                    </p>
                    <div className="space-y-2">
                      {kitItems.map((item, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <Input
                            placeholder="Tamanho (1 ano, 2 anos, P, M, G...)"
                            value={item.size}
                            onChange={(e) => updateKitSize(index, e.target.value)}
                            className="w-24"
                          />
                          <Input
                            type="number"
                            min="0"
                            placeholder="Qtd"
                            value={item.quantity}
                            onChange={(e) => updateKitQuantity(index, Number(e.target.value))}
                            className="w-20"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeKitSize(index)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addKitSize}
                        className="w-full"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Adicionar Tamanho
                      </Button>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm font-medium">
                        Total do Kit: {getTotalKitQuantity()} peças
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {kitItems.filter(item => item.quantity > 0).map(item => 
                          `${item.quantity} ${item.size}`
                        ).join(" | ")}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Seletor de Serviços */}
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Wrench className="w-4 h-4 text-green-600" />
                  <Label className="text-green-800 font-medium">Adicionar Serviço Rápido</Label>
                </div>
                <div className="flex gap-3">
                  <Select value={selectedServico} onValueChange={setSelectedServico}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Selecione um serviço pré-cadastrado" />
                    </SelectTrigger>
                    <SelectContent>
                      {servicos.map((servico) => (
                        <SelectItem key={servico.id} value={servico.id}>
                          {servico.nome}
                          {servico.preco_padrao > 0 && ` - R$ ${servico.preco_padrao.toFixed(2)}`}
                          {servico.categoria && ` (${servico.categoria})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    onClick={() => {
                      if (!selectedServico) {
                        toast.error("Selecione um serviço");
                        return;
                      }
                      const servico = servicos.find(s => s.id === selectedServico);
                      if (servico) {
                        // Adicionar serviço como um novo item no orçamento
                        const newItem = {
                          description: servico.nome + (servico.descricao ? ` - ${servico.descricao}` : ""),
                          quantity: 1,
                          value: servico.preco_padrao || 0,
                          category: currentItemCategory || "",
                          especificacoes: currentItemSpecificacoes || {}
                        };
                        setItems([...items, newItem]);
                        
                        // Só definir tipo como "outro" se o usuário ainda não tiver selecionado um tipo
                        if (!type || type.trim() === "") {
                          setType("outro");
                        }
                        toast.success(`Serviço "${servico.nome}" adicionado!`);
                        setSelectedServico("");
                      }
                    }}
                    disabled={!selectedServico}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar
                  </Button>
                </div>
                {servicos.length === 0 && (
                  <p className="text-xs text-green-700 mt-2">
                    Nenhum serviço cadastrado. <a href="/servicos" className="underline">Cadastre serviços aqui</a>
                  </p>
                )}
              </div>

              {/* Items */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Itens do Orçamento</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addItem}
                      className="border-secondary text-secondary"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Adicionar Item
                    </Button>
                  </div>
                </div>

                {/* Seletor de Categoria e Especificações (para Cortinas, etc) */}
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <Wrench className="w-4 h-4 text-purple-600" />
                    <Label className="text-purple-800 font-medium">Especificações do Item (opcional)</Label>
                  </div>
                  <div>
                    <Label htmlFor="itemCategory" className="text-sm">Categoria</Label>
                    <Select value={currentItemCategory} onValueChange={setCurrentItemCategory}>
                      <SelectTrigger id="itemCategory" className="bg-white mt-1">
                        <SelectValue placeholder="Selecione uma categoria (opcional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.name}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {currentItemCategory && (
                    <SpecificationFields
                      categoryName={currentItemCategory}
                      onSpecificationsChange={setCurrentItemSpecificacoes}
                      initialValues={currentItemSpecificacoes}
                    />
                  )}
                </div>

                {/* Seletor de Catálogo */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Package className="w-4 h-4 text-blue-600" />
                    <Label className="text-blue-800 font-medium">Adicionar do Catálogo</Label>
                  </div>
                  <div className="space-y-3">
                    <div className="flex gap-3">
                      <Popover open={productPopoverOpen} onOpenChange={setProductPopoverOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={productPopoverOpen}
                            className="flex-1 justify-between"
                          >
                            {selectedProduct
                              ? products.find((product) => product.id === selectedProduct)?.name || "Selecione um produto"
                              : "Selecione um produto do catálogo"}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[400px] p-0" align="start">
                          <Command>
                            <CommandInput 
                              placeholder="Pesquisar produto por nome, tipo ou material..." 
                              value={productSearchTerm}
                              onValueChange={setProductSearchTerm}
                            />
                            <CommandList>
                              <CommandEmpty>Nenhum produto encontrado.</CommandEmpty>
                              <CommandGroup>
                                {filteredProducts.map((product) => (
                                  <CommandItem
                                    key={product.id}
                                    value={`${product.name} ${product.type || ""} ${product.materials?.join(" ") || ""}`}
                                    onSelect={() => {
                                      setSelectedProduct(product.id);
                                      setProductPopoverOpen(false);
                                      setProductSearchTerm("");
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        selectedProduct === product.id ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                    <div className="flex flex-col">
                                      <span className="font-medium">{product.name}</span>
                                      <span className="text-xs text-muted-foreground">
                                        R$ {product.unit_price.toFixed(2)}
                                        {product.type && ` • ${product.type}`}
                                      </span>
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <Button
                        type="button"
                        onClick={openQuantityModal}
                        disabled={!selectedProduct}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Adicionar
                      </Button>
                      <NewProductModal
                        onProductCreated={() => {
                          // Recarregar produtos após criar novo
                        }}
                        triggerText="Criar novo produto"
                        triggerVariant="outline"
                      />
                    </div>
                  </div>
                  {selectedProduct && (
                    <div className="mt-3 text-sm text-blue-700 bg-blue-100 p-2 rounded">
                      {(() => {
                        const product = products.find(p => p.id === selectedProduct);
                        return product ? (
                          <div>
                            <p><strong>Nome:</strong> {product.name}</p>
                            <p><strong>Preço:</strong> R$ {product.unit_price.toFixed(2)}</p>
                            <p><strong>Tempo estimado:</strong> {product.work_hours}h</p>
                            <p><strong>Tipo:</strong> {product.type}</p>
                            {product.materials && product.materials.length > 0 && (
                              <p><strong>Materiais:</strong> {product.materials.join(", ")}</p>
                            )}
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}
                </div>

                {items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-3 p-4 rounded-lg border border-border">
                    <div className="col-span-12 md:col-span-5">
                      <div className="space-y-1">
                        <label className="text-xs text-muted-foreground">
                          Descrição <span className="text-gray-400">(opcional)</span>
                        </label>
                        <Input
                          placeholder="Descrição do item"
                          value={item.description}
                          onChange={(e) => {
                            const newItems = [...items];
                            newItems[index].description = e.target.value;
                            setItems(newItems);
                          }}
                          className="border-input"
                          title="💡 Opcional - A descrição do produto aparecerá automaticamente"
                        />
                      </div>
                    </div>
                    <div className="col-span-6 md:col-span-3">
                      <Input
                        type="number"
                        placeholder="Qtd"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => {
                          const newItems = [...items];
                          newItems[index].quantity = Number(e.target.value);
                          setItems(newItems);
                        }}
                        className="border-input"
                      />
                    </div>
                    <div className="col-span-6 md:col-span-3">
                      <div className="space-y-1">
                        <Input
                          type="number"
                          placeholder="Valor unitário"
                          step="0.01"
                          min="0"
                          value={item.value}
                          onChange={(e) => {
                            const newItems = [...items];
                            newItems[index].value = Number(e.target.value);
                            setItems(newItems);
                          }}
                          className="border-input"
                        />
                        <p className="text-xs text-muted-foreground">
                          Valor por unidade (ex: 25.50)
                        </p>
                      </div>
                    </div>
                    {item.category && item.especificacoes && Object.keys(item.especificacoes).length > 0 && (
                      <div className="col-span-12">
                        <SpecificationDisplay
                          categoryName={item.category}
                          specifications={item.especificacoes}
                        />
                      </div>
                    )}
                    {items.length > 1 && (
                      <div className="col-span-12 md:col-span-1 flex items-center justify-center">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(index)}
                          className="text-destructive hover:text-destructive/80"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}

                <PersonalizationListEditor
                  entries={personalizations}
                  onChange={setPersonalizations}
                  description="Registre cada peça personalizada com nome e tamanho. Esses dados ficam salvos no orçamento e podem ser reaproveitados no pedido."
                  availableSizes={clothingSizes}
                  onAddSize={addCustomSize}
                  isLoadingSizes={isSizesLoading}
                />

                <div className="flex justify-end p-4 bg-muted/30 rounded-lg">
                  <div className="text-right">
                    {discount > 0 && (
                      <>
                        <p className="text-sm text-muted-foreground line-through">
                          R$ {subtotal.toFixed(2)}
                        </p>
                        <p className="text-sm text-red-600 mb-2">
                          -{discountType === 'percentage' ? `${discount.toFixed(2)}%` : `R$ ${discountAmount.toFixed(2)}`}
                        </p>
                      </>
                    )}
                    <p className="text-sm text-muted-foreground">Total</p>
                    <p className="text-2xl font-bold text-foreground">
                      R$ {total.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="discountType">Tipo de Desconto</Label>
                  <Select value={discountType} onValueChange={(val: any) => setDiscountType(val)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Valor Fixo (R$)</SelectItem>
                      <SelectItem value="percentage">Percentual (%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="discount">Desconto {discountType === 'percentage' ? '(%)' : '(R$)'}</Label>
                  <Input
                    id="discount"
                    type="number"
                    min="0"
                    step={discountType === 'percentage' ? '0.01' : '0.01'}
                    value={discount}
                    onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                    placeholder={discountType === 'percentage' ? '0.00' : 'R$ 0,00'}
                  />
                </div>
              </div>

              {discount > 0 && subtotal > 0 && (
                <>
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-200">
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground font-semibold">Subtotal</p>
                        <p className="text-lg font-bold text-foreground">R$ {subtotal.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground font-semibold">
                          {discountType === 'percentage' ? 'Desconto (%)' : 'Desconto (R$)'}
                        </p>
                        <p className="text-lg font-bold text-red-600">
                          -{discountType === 'percentage' ? `${discount.toFixed(2)}%` : `R$ ${discount.toFixed(2)}`}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground font-semibold">Valor Final</p>
                        <p className="text-lg font-bold text-green-600">
                          R$ {(discountType === 'percentage'
                            ? subtotal * (1 - discount / 100)
                            : Math.max(0, subtotal - discount)
                          ).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="finalValue">Valor Final (Com Desconto) - R$</Label>
                    <Input
                      id="finalValue"
                      type="number"
                      step="0.01"
                      readOnly
                      className="border-input bg-green-50"
                      value={(discountType === 'percentage'
                        ? subtotal * (1 - discount / 100)
                        : Math.max(0, subtotal - discount)
                      ).toFixed(2)}
                    />
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="observations">Observações</Label>
                <Textarea
                  id="observations"
                  placeholder="Observações adicionais..."
                  rows={3}
                  className="border-input resize-none"
                />
              </div>

              <div className="flex flex-col-reverse gap-3 pt-4 border-t border-border sm:flex-row sm:items-center">
                <Button
                  type="submit"
                  className="bg-secondary hover:bg-secondary/90 text-secondary-foreground w-full sm:w-auto"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Salvar Orçamento
                </Button>
                <div className="space-y-3 w-full sm:w-auto">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-amber-600 text-xs font-bold">!</span>
                      </div>
                      <p className="text-amber-800 text-sm font-medium">
                        Atenção: Salve o orçamento antes de imprimir ou enviar pelo WhatsApp
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Button
                      type="button"
                      variant="outline"
                      className="border-secondary text-secondary w-full sm:w-auto"
                      onClick={handleOpenPublic}
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Visualizar
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="border-border w-full sm:w-auto"
                      onClick={handlePrint}
                    >
                      <Printer className="w-4 h-4 mr-2" />
                      Imprimir
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="border-green-600 text-green-600 hover:bg-green-600/10 w-full sm:w-auto"
                      onClick={handleWhatsApp}
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      WhatsApp
                    </Button>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/orcamentos")}
                  className="border-border w-full sm:w-auto"
                >
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>

        {/* Modal de Quantidade */}
        <Dialog open={quantityModalOpen} onOpenChange={setQuantityModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Definir Quantidade</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {selectedProduct && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">Produto selecionado:</p>
                  <p className="font-medium">
                    {products.find(p => p.id === selectedProduct)?.name} - 
                    {products.find(p => p.id === selectedProduct)?.type}
                  </p>
                  <p className="text-sm text-gray-500">
                    Preço: R$ {products.find(p => p.id === selectedProduct)?.unit_price?.toFixed(2)}
                  </p>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="quantity">Quantidade</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="1"
                  value={selectedQuantity}
                  onChange={(e) => setSelectedQuantity(Number(e.target.value))}
                  placeholder="Digite a quantidade"
                />
              </div>
              
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setQuantityModalOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={addCatalogItemWithQuantity}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Adicionar ao Orçamento
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal do WhatsApp */}
        <Dialog open={whatsappModalOpen} onOpenChange={setWhatsappModalOpen}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Personalizar Mensagem do WhatsApp</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="whatsapp-message">Mensagem</Label>
                <Textarea
                  id="whatsapp-message"
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Digite sua mensagem personalizada..."
                  rows={8}
                  className="resize-none"
                />
              </div>
              
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setWhatsappModalOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={() => setCustomMessage(generateDefaultMessage())}
                  variant="outline"
                >
                  Usar Modelo Padrão
                </Button>
                <Button
                  onClick={sendWhatsAppMessage}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Enviar WhatsApp
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal Criar Novo Cliente */}
        <Dialog open={showNewClientModal} onOpenChange={setShowNewClientModal}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Criar Novo Cliente</DialogTitle>
              <DialogDescription>
                Preencha os dados do cliente. Campos marcados com * são obrigatórios.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="new-client-name">Nome *</Label>
                  <Input
                    id="new-client-name"
                    value={newClientName}
                    onChange={(e) => setNewClientName(e.target.value)}
                    placeholder="Nome completo"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-client-phone">Telefone</Label>
                  <Input
                    id="new-client-phone"
                    value={newClientPhone}
                    onChange={(e) => setNewClientPhone(e.target.value)}
                    placeholder="(11) 99999-9999"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="new-client-email">E-mail</Label>
                  <Input
                    id="new-client-email"
                    type="email"
                    value={newClientEmail}
                    onChange={(e) => setNewClientEmail(e.target.value)}
                    placeholder="email@exemplo.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-client-cpf-cnpj">CPF/CNPJ</Label>
                  <Input
                    id="new-client-cpf-cnpj"
                    value={newClientCpfCnpj}
                    onChange={(e) => setNewClientCpfCnpj(e.target.value)}
                    placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-client-address">Endereço Completo</Label>
                <Textarea
                  id="new-client-address"
                  value={newClientAddress}
                  onChange={(e) => setNewClientAddress(e.target.value)}
                  placeholder="Rua, número, complemento, bairro, cidade - UF, CEP"
                  rows={2}
                />
              </div>

              <div className="border-t pt-4">
                <Label className="text-sm font-semibold mb-3 block flex items-center gap-2">
                  Endereço Detalhado (Campos Separados)
                  <span className="text-xs text-muted-foreground font-normal">Opcional - Facilita preenchimento</span>
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="new-client-logradouro">Logradouro</Label>
                    <Input
                      id="new-client-logradouro"
                      value={newClientEnderecoLogradouro}
                      onChange={(e) => setNewClientEnderecoLogradouro(e.target.value)}
                      placeholder="Rua, Avenida, etc."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-client-numero">Número</Label>
                    <Input
                      id="new-client-numero"
                      value={newClientEnderecoNumero}
                      onChange={(e) => setNewClientEnderecoNumero(e.target.value)}
                      placeholder="123"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-client-complemento">Complemento</Label>
                    <Input
                      id="new-client-complemento"
                      value={newClientEnderecoComplemento}
                      onChange={(e) => setNewClientEnderecoComplemento(e.target.value)}
                      placeholder="Apto, bloco, etc."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-client-bairro">Bairro</Label>
                    <Input
                      id="new-client-bairro"
                      value={newClientEnderecoBairro}
                      onChange={(e) => setNewClientEnderecoBairro(e.target.value)}
                      placeholder="Bairro"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-client-cidade">Cidade</Label>
                    <Input
                      id="new-client-cidade"
                      value={newClientEnderecoCidade}
                      onChange={(e) => setNewClientEnderecoCidade(e.target.value)}
                      placeholder="Cidade"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-client-uf">UF</Label>
                    <Input
                      id="new-client-uf"
                      value={newClientEnderecoUf}
                      onChange={(e) => setNewClientEnderecoUf(e.target.value.toUpperCase())}
                      placeholder="SP"
                      maxLength={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-client-cep">CEP</Label>
                    <Input
                      id="new-client-cep"
                      value={newClientEnderecoCep}
                      onChange={(e) => setNewClientEnderecoCep(e.target.value)}
                      placeholder="00000-000"
                    />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowNewClientModal(false);
                  // Limpar campos
                  setNewClientName("");
                  setNewClientPhone("");
                  setNewClientEmail("");
                  setNewClientCpfCnpj("");
                  setNewClientAddress("");
                  setNewClientEnderecoLogradouro("");
                  setNewClientEnderecoNumero("");
                  setNewClientEnderecoComplemento("");
                  setNewClientEnderecoBairro("");
                  setNewClientEnderecoCidade("");
                  setNewClientEnderecoUf("");
                  setNewClientEnderecoCep("");
                }}
                disabled={creatingClient}
              >
                Cancelar
              </Button>
              <Button
                onClick={async () => {
                  if (!newClientName.trim()) {
                    toast.error("Nome do cliente é obrigatório");
                    return;
                  }

                  setCreatingClient(true);
                  try {
                    const empresa_id = empresa?.id;
                    if (!empresa_id) {
                      toast.error("Erro ao obter dados da empresa");
                      return;
                    }

                    const { data, error } = await supabase
                      .from("customers")
                      .insert({
                        empresa_id,
                        name: newClientName.trim(),
                        phone: newClientPhone.trim() || null,
                        email: newClientEmail.trim() || null,
                        address: newClientAddress.trim() || null,
                        cpf_cnpj: newClientCpfCnpj.trim() || null,
                        endereco_logradouro: newClientEnderecoLogradouro.trim() || null,
                        endereco_numero: newClientEnderecoNumero.trim() || null,
                        endereco_complemento: newClientEnderecoComplemento.trim() || null,
                        endereco_bairro: newClientEnderecoBairro.trim() || null,
                        endereco_cidade: newClientEnderecoCidade.trim() || null,
                        endereco_uf: newClientEnderecoUf.trim() || null,
                        endereco_cep: newClientEnderecoCep.trim() || null,
                      })
                      .select()
                      .single();

                    if (error) {
                      console.error("Erro ao criar cliente:", error);
                      toast.error(error.message || "Erro ao criar cliente");
                      return;
                    }

                    toast.success("Cliente criado com sucesso!");
                    setClientName(newClientName.trim());
                    if (newClientPhone.trim()) {
                      setClientPhone(newClientPhone.trim());
                    }
                    setShowNewClientModal(false);
                    
                    // Limpar campos
                    setNewClientName("");
                    setNewClientPhone("");
                    setNewClientEmail("");
                    setNewClientCpfCnpj("");
                    setNewClientAddress("");
                    setNewClientEnderecoLogradouro("");
                    setNewClientEnderecoNumero("");
                    setNewClientEnderecoComplemento("");
                    setNewClientEnderecoBairro("");
                    setNewClientEnderecoCidade("");
                    setNewClientEnderecoUf("");
                    setNewClientEnderecoCep("");

                    // Invalidar cache de clientes
                    queryClient.invalidateQueries({ queryKey: ["customers"] });
                  } catch (err: any) {
                    console.error("Erro ao criar cliente:", err);
                    toast.error(err.message || "Erro ao criar cliente");
                  } finally {
                    setCreatingClient(false);
                  }
                }}
                disabled={creatingClient || !newClientName.trim()}
              >
                {creatingClient ? "Criando..." : "Criar Cliente"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}